import { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  netural400,
  neturalBlack,
  primaryGrey,
  secondary500,
} from "../../../constants/color";
import DraftBanner from "../../../components/draft-banner/draft-banner.component";
import WheelPreview from "../../../components/wheel-preview/wheel-preview.component";
import RewardTypePicker from "../../../components/reward-type-picker/reward-type-picker.component";
import SliceConfigurationRow from "../../../components/slice-configuration-row/slice-configuration-row.component";
import ProductSelectionModal from "../../../components/product-selection-modal/product-selection-modal.component";
import WheelSectionCard from "../../../components/wheel-section-card/wheel-section-card.component";
import DuplicateToggle from "../../../components/duplicate-toggle/duplicate-toggle.component";
import {
  FETCH_REWARD_WHEEL_DETAILS,
  UPDATE_REWARD_WHEEL,
} from "../../../services/graphql/admin/rewardWheel";
import useSliceManager from "./useSliceManager";
import {
  SLICE_COLORS,
  DISPLAYED_SLICES_OPTIONS,
  detectPattern,
  formatDateForInput,
  buildSliceInput,
  buildSlicesPayload,
  validateSlices,
  isSlicesValid,
  isZeroPricedSlice,
  formatValidationError,
} from "./rewardWheel.constants";

const US_COUNTRY_CODE = "US";

const RewardWheelDetailPage = () => {
  const { wheelId } = useParams();
  const navigate = useNavigate();
  const businesprofile = useSelector((state) => state.businesprofile);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // General settings (READ-ONLY)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timezone, setTimezone] = useState("");

  // Eligibility (READ-ONLY)
  const [minimumPurchase, setMinimumPurchase] = useState("");

  // Location targeting (READ-ONLY)
  const [state, setState] = useState("");
  const [city, setCity] = useState("");

  // Reward type (READ-ONLY)
  const [rewardType, setRewardType] = useState("Discount");

  // Status (READ-ONLY)
  const [status, setStatus] = useState("Draft");

  // Presentation (READ-ONLY: original slice count for dropdown filtering)
  const [originalSlicesCount, setOriginalSlicesCount] = useState(0);

  // Product selection modal
  const [modalSliceIndex, setModalSliceIndex] = useState(null);

  // Validation errors (same shape as create page)
  const [errors, setErrors] = useState({});

  // Slice state via shared hook (initialised with 0 slices; overridden by API data)
  const {
    slices,
    displayedSlices,
    duplicateSlice,
    setDuplicateSlice,
    initSlices,
    handleDisplayedSlicesChange,
    handleDuplicateToggle,
    handleSliceChange,
    handleProductSelected,
  } = useSliceManager(0, false);

  // Calculate if duplicate button should be disabled
  const calculateDuplicateDisabled = () => {
    // Count unique products
    const uniqueProductIds = new Set(
      slices
        .slice(0, displayedSlices)
        .filter((slice) => slice.productId)
        .map((slice) => slice.productId),
    );

    // Only disable when trying to turn it ON (not already checked)
    // Allow turning OFF even with 0 products to unlock fields
    if (duplicateSlice) {
      return false; // Always allow unchecking
    }

    // Disable if trying to turn it ON and:
    // 1. No products selected (0 unique products)
    // 2. All slices have different products (equal to displayedSlices)
    return (
      uniqueProductIds.size === 0 || uniqueProductIds.size === displayedSlices
    );
  };

  // Auto-uncheck duplicate if no products left
  useEffect(() => {
    if (duplicateSlice) {
      const uniqueProductIds = new Set(
        slices
          .slice(0, displayedSlices)
          .filter((slice) => slice.productId)
          .map((slice) => slice.productId),
      );

      if (uniqueProductIds.size === 0) {
        setDuplicateSlice(false);
      }
    }
  }, [slices, displayedSlices, duplicateSlice, setDuplicateSlice]);

  // Fetch wheel details
  const [fetchRewardWheelDetails] = useLazyQuery(FETCH_REWARD_WHEEL_DETAILS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.fetchRewardWheelDetails) {
        const wheel = data.fetchRewardWheelDetails;
        setStartDate(wheel.startDate);
        setEndDate(wheel.endDate);
        setTimezone(wheel.timezone || "America/New_York");
        setMinimumPurchase(wheel.minimumPurchase || "");
        setState(wheel.state);
        setCity(wheel.city);
        setDuplicateSlice(wheel.shouldDuplicate || false);
        setRewardType(wheel.rewardType);
        setStatus(wheel.status);

        const wheelsArray = Array.isArray(wheel.slices) ? wheel.slices : [];
        const flatSlices = wheelsArray.map((slice) => ({
          productSearch: slice.productName
            ? slice.variantName
              ? `${slice.productName} — ${slice.variantName}`
              : slice.productName
            : "",
          price: slice.price,
          productId: slice.productId || null,
          productName: slice.productName || "",
          variantId: slice.variantId || null,
          variantName: slice.variantName || "",
          discount: slice.discount || "",
          businessId: slice.businessId || null,
        }));

        const count = wheel.displayedSlices;
        initSlices(flatSlices, count);
        setOriginalSlicesCount(flatSlices.length);
      }
      setIsLoading(false);
    },
    onError: (error) => {
      setIsLoading(false);
      toast.error(error.message, {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
    },
  });

  // Update wheel mutation
  const [updateRewardWheel, { loading: isSubmitting }] = useMutation(
    UPDATE_REWARD_WHEEL,
    {
      fetchPolicy: "no-cache",
      onCompleted(data) {
        if (data?.updateRewardWheel?.success) {
          toast.success(
            data.updateRewardWheel.message || "Reward wheel updated!",
            { position: "top-center", autoClose: 3000, theme: "colored" },
          );
          navigate("/admin/reward/list");
        } else {
          toast.error(
            data?.updateRewardWheel?.message || "Failed to update reward wheel",
            { position: "top-center", autoClose: 3000, theme: "colored" },
          );
        }
      },
      onError(error) {
        // Format GraphQL validation errors into user-friendly messages
        // See formatValidationError() in rewardWheel.constants.js for customization
        const userFriendlyMessage = formatValidationError(error.message);
        toast.error(userFriendlyMessage, {
          style: { width: "450px" },
          position: "top-center",
          autoClose: 3000,
          theme: "colored",
        });
      },
    },
  );

  // Fetch wheel details on mount
  useEffect(() => {
    fetchRewardWheelDetails({
      variables: { wheelId },
    });
  }, [wheelId]);

  const validate = () => {
    const newErrors = {};

    const { sliceErrors, duplicateNoProduct } = validateSlices(
      slices,
      displayedSlices,
      duplicateSlice,
      rewardType,
    );

    if (duplicateNoProduct) newErrors.duplicateNoProduct = duplicateNoProduct;
    if (Object.keys(sliceErrors).length > 0) newErrors.slices = sliceErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () =>
    isSlicesValid(slices, displayedSlices, duplicateSlice, rewardType);

  const handleSubmit = async () => {
    if (!validate() || isSubmitting) return;

    const slicesToSend = buildSlicesPayload(
      slices,
      displayedSlices,
      duplicateSlice,
    );

    const input = {
      wheelId,
      displayedSlices,
      shouldDuplicate: duplicateSlice,
      slices: slicesToSend.map((s) =>
        buildSliceInput(s, rewardType, businesprofile?.businessId),
      ),
    };
    await updateRewardWheel({ variables: { input } });
  };

  if (isLoading) {
    return (
      <div className="main-div-right-section">
        <div className="second-section-style">
          <p>Loading wheel details...</p>
        </div>
      </div>
    );
  }

  const patternLength = detectPattern(slices, displayedSlices).length;
  const normalizedStatus = (status || "").toLowerCase();
  const canUpdateGame =
    normalizedStatus === "active" ||
    normalizedStatus === "scheduled";
  const bannerTitle = status || "Wheel";
  const bannerSubtitleByStatus = {
    scheduled: "This game is scheduled. You can review and update it before it goes live.",
    active: "This game is active. Updates will affect upcoming customer spins.",
    completed: "This game is completed and is now read-only.",
    cancelled: "This game is cancelled and can no longer be updated.",
  };
  const bannerSubtitle =
    bannerSubtitleByStatus[normalizedStatus] ||
    "Review this game configuration and details.";
  const isReadOnlyView =
    normalizedStatus === "completed" || normalizedStatus === "cancelled";
  const pageModeLabel = isReadOnlyView ? "View Game" : "Edit Game";

  return (
    <div className="main-div-right-section">
      <div className="second-section-style">
        {/* Breadcrumb */}
        <div style={{ marginBottom: "8px" }}>
          <Link
            to="/admin/reward/list"
            style={{
              fontSize: "16px",
              color: netural400,
              textDecoration: "underline",
            }}
          >
            Reward Wheel
          </Link>
          <label
            style={{ fontSize: "14px", color: netural400, margin: "0 8px" }}
          >
            &gt;
          </label>
          <label
            style={{ fontSize: "16px", color: neturalBlack, fontWeight: "600" }}
          >
            {pageModeLabel}
          </label>
        </div>
        <label
          style={{
            fontSize: "22px",
            fontWeight: "bold",
            color: neturalBlack,
            marginBottom: "24px",
            display: "block",
          }}
        >
          {pageModeLabel}
        </label>

        <DraftBanner
          title={bannerTitle}
          subtitle={bannerSubtitle}
          actionLabel={canUpdateGame ? (isSubmitting ? "Updating..." : "Update game") : ""}
          onAction={handleSubmit}
          actionDisabled={isSubmitting || !isFormValid()}
        />

        <Row>
          {/* LEFT COLUMN */}
          <Col lg={6} md={6} style={{ paddingLeft: 0 }}>
            {/* General Settings - READ-ONLY */}
            <WheelSectionCard title="General Settings">
              <Row>
                <Col lg={6} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ fontSize: "14px", color: neturalBlack }}
                    >
                      Start date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formatDateForInput(startDate)}
                      disabled
                      style={{
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col lg={6} md={6}>
                  <Form.Group>
                    <Form.Label
                      style={{ fontSize: "14px", color: neturalBlack }}
                    >
                      End date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formatDateForInput(endDate)}
                      disabled
                      style={{
                        borderRadius: "10px",
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: secondary500,
                  }}
                />
                <label
                  style={{
                    fontSize: "13px",
                    color: secondary500,
                    fontWeight: "600",
                  }}
                >
                  Timezone: {timezone || "America/New_York"} (Business Time)
                </label>
              </div>
              <label
                style={{
                  fontSize: "12px",
                  color: netural400,
                  marginTop: "8px",
                  display: "block",
                }}
              >
                If a campaign ends while a customer is at checkout, the reward
                remains valid until the redemption window expires.
              </label>
            </WheelSectionCard>

            {/* Eligibility Rules - READ-ONLY */}
            <WheelSectionCard title="Eligibility Rules">
              <Form.Label style={{ fontSize: "14px", color: netural400 }}>
                Minimum purchase amount required
              </Form.Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${primaryGrey}`,
                  borderRadius: "10px",
                  padding: "8px 12px",
                  maxWidth: "200px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <label
                  style={{
                    fontSize: "16px",
                    color: netural400,
                    marginRight: "8px",
                  }}
                >
                  $
                </label>
                <input
                  type="number"
                  value={minimumPurchase}
                  disabled
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    fontSize: "14px",
                    color: netural400,
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </div>
              <label
                style={{
                  fontSize: "12px",
                  color: netural400,
                  marginTop: "8px",
                  display: "block",
                }}
              >
                Minimum amount spent on order to activate reward
              </label>
            </WheelSectionCard>

            {/* Location Targeting - READ-ONLY */}
            <WheelSectionCard
              title="Location Targeting"
              subtitle="Choose the city where customers must be located to see and redeem the reward if it gets selected."
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${primaryGrey}`,
                  borderRadius: "10px",
                  padding: "8px 12px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <input
                  type="text"
                  value={state}
                  disabled
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    fontSize: "14px",
                    color: netural400,
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  border: `1px solid ${primaryGrey}`,
                  borderRadius: "10px",
                  padding: "8px 12px",
                  backgroundColor: "#f5f5f5",
                  marginTop: "3%",
                }}
              >
                <input
                  type="text"
                  value={city}
                  disabled
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    fontSize: "14px",
                    color: netural400,
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </div>
            </WheelSectionCard>
          </Col>

          {/* RIGHT COLUMN */}
          <Col lg={6} md={6} style={{ paddingRight: 0 }}>
            {/* Presentation */}
            <WheelSectionCard title="Presentation">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <WheelPreview
                  sliceColors={SLICE_COLORS}
                  displayedSlices={displayedSlices}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#F9F9F9",
                  borderRadius: "10px",
                  padding: "10px 16px",
                }}
              >
                <label style={{ fontSize: "14px", color: neturalBlack }}>
                  Displayed slices
                </label>
                <Form.Select
                  value={displayedSlices}
                  onChange={(e) =>
                    handleDisplayedSlicesChange(Number(e.target.value))
                  }
                  style={{
                    width: "80px",
                    borderRadius: "8px",
                    border: `1px solid ${primaryGrey}`,
                  }}
                >
                  {DISPLAYED_SLICES_OPTIONS.filter(
                    (opt) => opt >= Math.max(5, originalSlicesCount),
                  ).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </WheelSectionCard>

            {/* Reward Type - READ-ONLY */}
            {/* <WheelSectionCard title="Reward Type">
              <RewardTypePicker
                selectedType={rewardType}
                onTypeChange={() => {}}
                disabled
              />
            </WheelSectionCard> */}

            {/* Slice Configuration - EDITABLE */}
            <WheelSectionCard
              title="Slice Configuration"
              subtitle="Choose which product can be rewarded and set the discount if it gets selected."
            >
              <DuplicateToggle
                checked={duplicateSlice}
                onChange={handleDuplicateToggle}
                disabled={calculateDuplicateDisabled()}
              />

              {errors.duplicateNoProduct && (
                <label
                  style={{
                    fontSize: "12px",
                    color: "#EA1010",
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  {errors.duplicateNoProduct}
                </label>
              )}

              {slices.slice(0, displayedSlices).map((slice, index) => (
                <SliceConfigurationRow
                  key={index}
                  sliceNumber={index + 1}
                  color={SLICE_COLORS[index % SLICE_COLORS.length]}
                  productSearch={slice.productSearch}
                  discount={slice.discount}
                  onProductSearchChange={(val) =>
                    handleSliceChange(index, "productSearch", val)
                  }
                  onDiscountChange={(val) =>
                    handleSliceChange(index, "discount", val)
                  }
                  onProductInputClick={() => setModalSliceIndex(index)}
                  disabled={duplicateSlice && index >= patternLength}
                  discountDisabled={rewardType === "Discount" && isZeroPricedSlice(slice)}
                  showDiscount={rewardType === "Discount"}
                  productError={errors.slices?.[index]?.productSearch}
                  discountError={errors.slices?.[index]?.discount}
                />
              ))}
            </WheelSectionCard>
          </Col>
        </Row>

        <ProductSelectionModal
          show={modalSliceIndex !== null}
          onHide={() => setModalSliceIndex(null)}
          onProductSelected={(selected) =>
            handleProductSelected(modalSliceIndex, selected, () =>
              setModalSliceIndex(null),
            )
          }
        />
      </div>
    </div>
  );
};

export default RewardWheelDetailPage;
